import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Link2, MessageCircle, Facebook, Linkedin, Mail, Calculator, Scale, TrendingUp, TrendingDown, Home, PiggyBank, Percent } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { blogPosts, SEMPLIFICAPP_URL } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINK_PHRASES = [
  ["cedolare secca", "cedolare"],
  ["regime forfettario", "forfettario"],
  ["forfettario", "forfettario"],
  ["deposito cauzionale", "deposito-cauzionale"],
  ["caparra", "caparra"],
  ["inquilino moroso", "inquilino-moroso"],
  ["morosità", "morosita"],
  ["sfratto", "sfratto"],
  ["fideiussione", "fideiussione"],
  ["canone concordato", "concordato"],
  ["contratto breve", "contratto-breve"],
  ["affitti brevi", "affitti-brevi"],
  ["classe energetica", "classe-energetica"],
  ["pertinenze", "pertinenza"],
  ["residenza fiscale", "residenza-fiscale"],
  ["contratto preliminare", "preliminar"],
  ["subentro", "subentro"],
  ["riforma del condominio", "riforma-del-condominio"],
  ["spurgo", "spurgo"],
  ["cancello", "cancello"],
  ["comproprietà", "compropriet"],
  ["stranieri", "stranieri"],
  ["transitorio", "transitori"],
  ["fotografie", "foto"],
];

const linkifyBody = (post, allPosts) => {
  if (!post) return [];
  const map = LINK_PHRASES.map(([phrase, frag]) => {
    const target = allPosts.find((x) => x.slug.includes(frag) && x.slug !== post.slug);
    return target ? [phrase, target.slug] : null;
  }).filter(Boolean);
  let used = 0;
  const usedSlugs = new Set();
  return post.body.map((par) => {
    if (par.startsWith("## ")) return { h2: true, text: par.slice(3) };
    if (par.startsWith("::highlight")) {
      const lines = par.split("\n").slice(1);
      const rows = lines
        .filter((l) => l.startsWith("- "))
        .map((l) => {
          const parts = l.slice(2).split("|");
          return {
            value: parts[0]?.trim(),
            label: parts[1]?.trim(),
            icon: parts[2]?.trim(),
          };
        });
      const note = lines.find((l) => l.startsWith("! "))?.slice(2).trim();
      return { highlight: true, rows, note };
    }
    let nodes = [par];
    // link in formato markdown: [testo](https://...) esterni, [testo](/percorso) interni
    const withMdLinks = [];
    for (const node of nodes) {
      if (typeof node !== "string") { withMdLinks.push(node); continue; }
      const re = /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]+)\)/g;
      let last = 0;
      let m;
      while ((m = re.exec(node)) !== null) {
        if (m.index > last) withMdLinks.push(node.slice(last, m.index));
        if (m[2].startsWith("/")) {
          withMdLinks.push({ mdlink: m[2], text: m[1] });
        } else {
          withMdLinks.push({ ext: m[2], text: m[1] });
        }
        last = m.index + m[0].length;
      }
      if (last < node.length) withMdLinks.push(node.slice(last));
    }
    nodes = withMdLinks;
    if (used < 3) {
      for (const [phrase, slug] of map) {
        if (used >= 3 || usedSlugs.has(slug)) break;
        const next = [];
        for (const node of nodes) {
          if (typeof node !== "string") {
            next.push(node);
            continue;
          }
          const idx = node.toLowerCase().indexOf(phrase);
          if (idx === -1 || used >= 3) {
            next.push(node);
            continue;
          }
          used += 1;
          usedSlugs.add(slug);
          next.push(
            node.slice(0, idx),
            { link: slug, text: node.slice(idx, idx + phrase.length) },
            node.slice(idx + phrase.length)
          );
        }
        nodes = next;
      }
    }
    return { h2: false, nodes };
  });
};
const setMeta = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  const prev = el?.getAttribute("content");
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-article-meta", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return prev;
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [post, setPost] = useState(() => blogPosts.find((p) => p.slug === slug));
  const [allPosts, setAllPosts] = useState(blogPosts);
  const [cat, setCat] = useState("");

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/blog/${slug}`)
      .then(({ data }) => {
        if (active) setPost(data);
      })
      .catch(() => {});
    axios
      .get(`${API}/blog`)
      .then(({ data }) => {
        if (active && Array.isArray(data) && data.length > 0) setAllPosts(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    if (post) setCat(post.category);
  }, [post]);

  const categories = [...new Set(allPosts.map((p) => p.category))];
  const explorer = allPosts.filter((p) => p.slug !== slug && (cat === "Tutte" || !cat || p.category === cat)).slice(0, 6);
  const linkedBody = useMemo(() => linkifyBody(post, allPosts), [post, allPosts]);

  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    document.title = `${post.title} | Blog Semplificaffitto`;
    meta?.setAttribute("content", post.excerpt);

    const origin = window.location.origin;
    const url = `${origin}/blog/${post.slug}`;
    const img = post.img ? (post.img.startsWith("http") ? post.img : `${origin}${post.img}`) : `${origin}/assets/hero-casa.jpg`;
    const ogPrevs = [
      ["property", "og:title", post.title],
      ["property", "og:description", post.excerpt],
      ["property", "og:image", img],
      ["property", "og:url", url],
      ["property", "og:type", "article"],
      ["property", "article:published_time", post.date || post.created_at || ""],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", post.title],
      ["name", "twitter:description", post.excerpt],
      ["name", "twitter:image", img],
    ].map(([attr, key, content]) => [attr, key, setMeta(attr, key, content)]);

    let canon = document.querySelector('link[rel="canonical"]');
    const prevCanon = canon?.getAttribute("href");
    const canonCreated = !canon;
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", url);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.img ? `https://www.semplificaffitto.it${post.img}` : undefined,
      datePublished: post.date || post.created_at,
      dateModified: post.updated_at || post.created_at,
      author: { "@type": "Organization", name: "Semplificaffitto.it", url: "https://www.semplificaffitto.it" },
      publisher: {
        "@type": "Organization",
        name: "Semplificaffitto.it",
        url: "https://www.semplificaffitto.it",
        logo: { "@type": "ImageObject", url: "https://www.semplificaffitto.it/assets/logo-nuovo.png" },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.semplificaffitto.it/blog/${post.slug}` },
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
      ogPrevs.forEach(([attr, key, prev]) => {
        const el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!el) return;
        if (prev == null || el.getAttribute("data-article-meta")) el.remove();
        else el.setAttribute("content", prev);
      });
      const canonEl = document.querySelector('link[rel="canonical"]');
      if (canonEl) {
        if (canonCreated) canonEl.remove();
        else if (prevCanon) canonEl.setAttribute("href", prevCanon);
      }
      ld.remove();
    };
  }, [post]);

  if (!post) {
    return (
      <main className="bg-paper min-h-screen">
        <Nav />
        <div className="pt-40 pb-24 text-center px-5">
          <h1 className="font-serif font-bold text-4xl mb-6">Articolo non trovato</h1>
          <Link to="/" className="text-brandblue font-semibold" data-testid="article-not-found-home-link">
            Torna alla home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="bg-paper" data-testid="article-page">
      <Nav />
      <article className="max-w-3xl mx-auto px-5 lg:px-8 pt-36 lg:pt-44 pb-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <Link
            to="/"
            data-testid="article-back-btn"
            className="inline-flex items-center gap-2 text-sm text-ink/50 hover:text-brandblue transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Tutti gli articoli
          </Link>
          <span className="block text-[11px] uppercase tracking-[0.2em] font-semibold text-brandblue">{post.category}</span>
          <h1 className="font-serif font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] mt-3 mb-6" data-testid="article-title">
            {post.title}
          </h1>
          <p className="text-xs text-ink/40 uppercase tracking-wider mb-10">
            {post.date && (
              <span className="normal-case" data-testid="article-date">
                {new Date(post.date).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                {" · "}
              </span>
            )}
            Blog Semplificaffitto · {post.read} di lettura
          </p>
          <img src={post.img} alt={post.title} className="w-full rounded-3xl border border-ink/10 shadow-lg mb-10 object-cover" />
          <p className="font-serif italic text-xl lg:text-2xl text-ink/75 leading-relaxed border-l-4 border-azure pl-6 mb-10">
            {post.excerpt}
          </p>
          {linkedBody.map((block, i) =>
            block.h2 ? (
              <h2 key={i} data-testid={`article-subtitle-${i}`} className="font-serif font-bold text-2xl lg:text-3xl text-ink mt-10 mb-4">
                {block.text}
              </h2>
            ) : block.highlight ? (
              <div
                key={i}
                data-testid={`article-highlight-${i}`}
                className="my-8 rounded-3xl border border-azure/25 bg-gradient-to-br from-azure/10 via-sky-50 to-emerald-50 p-6 sm:p-8 shadow-sm"
              >
                <div className={`grid gap-4 ${block.rows.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                  {block.rows.map((r, k) => {
                    const IconMap = { calculator: Calculator, scale: Scale, up: TrendingUp, down: TrendingDown, home: Home, piggy: PiggyBank, percent: Percent };
                    const Icon = r.icon ? IconMap[r.icon] : null;
                    return (
                      <div key={k} className="rounded-2xl bg-white/80 border border-azure/15 px-5 py-4 flex flex-col items-center text-center">
                        {Icon && <Icon size={28} className="text-azure mb-2" strokeWidth={1.8} />}
                        <p className="font-serif font-bold text-3xl lg:text-4xl text-brandblue leading-tight">{r.value}</p>
                        <p className="mt-1 text-sm text-ink/60 leading-snug">{r.label}</p>
                      </div>
                    );
                  })}
                </div>
                {block.note && (
                  <p className="mt-5 text-sm lg:text-base text-ink/70 leading-relaxed border-l-4 border-emerald-500/60 pl-4">{block.note}</p>
                )}
              </div>
            ) : (
              <p key={i} className="text-base lg:text-lg text-ink/70 leading-relaxed mb-6 text-justify [hyphens:auto]" data-testid={`article-paragraph-${i}`}>
                {block.nodes.map((node, j) =>
                  typeof node === "string" ? (
                    node
                  ) : node.mdlink ? (
                    <Link
                      key={j}
                      to={node.mdlink}
                      data-testid={`article-md-link-${j}`}
                      className="text-brandblue underline decoration-azure/40 underline-offset-2 hover:text-azure transition-colors"
                    >
                      {node.text}
                    </Link>
                  ) : node.ext ? (
                    <a
                      key={j}
                      href={node.ext}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`article-external-link-${j}`}
                      className="text-brandblue underline decoration-azure/40 underline-offset-2 hover:text-azure transition-colors"
                    >
                      {node.text}
                    </a>
                  ) : (
                    <Link
                      key={j}
                      to={`/blog/${node.link}`}
                      data-testid={`article-internal-link-${node.link}`}
                      className="text-brandblue underline decoration-azure/40 underline-offset-2 hover:text-azure transition-colors"
                    >
                      {node.text}
                    </Link>
                  )
                )}
              </p>
            )
          )}

          <div className="mt-10 pt-8 border-t border-ink/10" data-testid="article-share-block">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-ink/40 mb-4">Condividi questo articolo</p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(window.location.href);
                    toast.success("Link copiato negli appunti!");
                  } catch {
                    toast.error("Impossibile copiare il link.");
                  }
                }}
                data-testid="share-copy-link-btn"
                aria-label="Copia link"
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-medium text-ink/70 hover:bg-ink hover:text-white transition-colors duration-300"
              >
                <Link2 size={16} />
                Copia link
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(post.title + " — ")}${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-whatsapp-btn"
                aria-label="Condividi su WhatsApp"
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-medium text-ink/70 hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors duration-300"
              >
                <MessageCircle size={16} />
                WhatsApp
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-facebook-btn"
                aria-label="Condividi su Facebook"
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-medium text-ink/70 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-colors duration-300"
              >
                <Facebook size={16} />
                Facebook
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-linkedin-btn"
                aria-label="Condividi su LinkedIn"
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-medium text-ink/70 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-colors duration-300"
              >
                <Linkedin size={16} />
                LinkedIn
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`}
                data-testid="share-email-btn"
                aria-label="Condividi via email"
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-medium text-ink/70 hover:bg-brandblue hover:text-white hover:border-brandblue transition-colors duration-300"
              >
                <Mail size={16} />
                Email
              </a>
            </div>
          </div>

          <div className="mt-12 rounded-3xl bg-ink text-white p-8 lg:p-10">
            <h3 className="font-serif font-bold text-2xl mb-3">Vuoi affittare senza pensieri?</h3>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Indennizzo del 100% del canone fino a 3 mensilità, inquilini selezionati e tutela legale inclusa. Richiedi una valutazione gratuita del tuo immobile.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/"
                onClick={() => sessionStorage.setItem("scrollTo", "#contatti")}
                data-testid="article-cta-contact"
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-white transition-colors duration-300"
              >
                Richiedi valutazione gratuita
                <ArrowUpRight size={16} />
              </Link>
              <a
                href={SEMPLIFICAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-cta-reserved"
                className="inline-flex items-center gap-2 border border-white/25 text-white/85 rounded-full px-6 py-3.5 text-sm hover:bg-white/10 transition-colors duration-300"
              >
                Area Riservata
              </a>
            </div>
          </div>
        </motion.div>

        <div className="mt-16" data-testid="blog-explorer">
          <h2 className="font-serif font-bold text-2xl mb-6">Continua a esplorare il blog</h2>
          <div className="flex flex-wrap gap-2.5 mb-8" data-testid="explorer-category-filters">
            {["Tutte", ...categories].map((c) => (
              <button
                key={c}
                data-testid={`explorer-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setCat(c)}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-300 border ${
                  (cat || "Tutte") === c || (!cat && c === "Tutte")
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-ink/60 border-ink/15 hover:border-azure hover:text-brandblue"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {explorer.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`explorer-card-${r.slug}`} className="group">
                <img src={r.img} alt={r.title} loading="lazy" className="w-full h-32 object-cover rounded-xl border border-ink/10 mb-1 transition-transform duration-500 group-hover:scale-[1.03]" />
                <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-brandblue mt-3">{r.category}</p>
                <p className="font-serif font-semibold text-sm leading-snug group-hover:text-brandblue transition-colors mt-1">{r.title}</p>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to="/"
              onClick={() => sessionStorage.setItem("scrollTo", "#blog")}
              data-testid="explorer-all-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brandblue hover:text-azure transition-colors"
            >
              Tutte le notizie ({allPosts.length})
              <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}
